import React, {useEffect, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import Toast from 'react-native-toast-message';
import {ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import Button from '../../../components/themed/Button';
import {ERROR_MESSAGE} from '../../../components/ToastBottom';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import Markdown from '../../../components/ui-controls/Markdown';
import Text from '../../../components/ui-controls/Text';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {OfferDetailStore} from '../../../stores/offersStore';
import {useStores} from '../../../stores/use-stores';
import theme from '../../../style/theme';
import rewardsTheme from '../../../theme/rewardsTheme';
import LastMinuteOptionTileCard from '../../cart/components/LastMinuteOptionTileCard';

import CoffeeCard from './CoffeeCard';

type ExclusiveOfferDetailScreenStandaloneProps = {
  offerDetails?: OfferDetailStore;
  isLoading?: boolean;
  navigation: any;
};
export const ExclusiveOfferDetailScreenStandalone = observer(
  ({
    offerDetails,
    isLoading,
    navigation,
  }: ExclusiveOfferDetailScreenStandaloneProps) => {
    const {cartStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();
    const isApplied = cartStore.activatedDeals.includes(offerDetails?.offer.mflId || '');

    if (isLoading) {
      return <ActivityIndicator />;
    } else if (!offerDetails?.offer) {
      return (
        <View>
          <Text>Not found</Text>
        </View>
      );
    }

    const showErrorToast = (message: string) =>
      Toast.show({
        type: ERROR_MESSAGE,
        text1: message,
      });
    const disableApplyButton = () => {
      if (
        offerDetails.offer.mflId.includes('LM') // It isn't an MFL offer ID
        || (offerDetails.offer.offerTrigger === 'Coffee Card'
          && (
            offerDetails.offer.coffeeCardStats.fully_redeemed // When fully redeemed, offer can no longer be used
            || offerDetails.offer.coffeeCardStats.purchased_quantity < offerDetails.offer.coffeeCardStats.target_quantity
          ))
      ) {
        return true;
      }
      return offerDetails.isApplyLoading || isApplied;
    };

    const image = isWebDesktopScreen
      ? offerDetails?.offer?.heroImageDesktop?.[0]
      : offerDetails?.offer?.heroImage?.[0];

    return (
      <ContentView testID="exclusive-offer-detail-screen">
        <ScrollView
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}>
          {image?.url ? (
            <Image
              source={{uri: image.url}}
              style={{
                width: '100%',
                height: undefined,
                aspectRatio:
                  image.width && image.height
                    ? image.width / image.height
                    : 1.52,
              }}
              resizeMode="cover"
            />
          ) : null}

          <View style={styles.textCard}>
            <Text style={styles.headline}>{offerDetails?.offer?.headline}</Text>
            <Markdown>{offerDetails?.offer?.description}</Markdown>
          </View>

          {offerDetails?.offer?.offerTrigger === 'Coffee Card' && <CoffeeCard offer={offerDetails.offer}/>}

          <Button
            onPress={async () => {
              /**
               * this error handling is not implemented
               */
              const {error} = await offerDetails.applyOffer();
              if (error) {
                showErrorToast(error);
              }
            }}
            loading={offerDetails.isApplyLoading}
            disabled={disableApplyButton()}
            title={isApplied ? 'APPLIED' : 'APPLY'}
            buttonStyle={styles.enterHereButton}
          />
          <ThemeProvider theme={rewardsTheme}>
            <FlatList
              data={offerDetails.linkedProducts}
              ItemSeparatorComponent={ItemSeparatorComponent}
              style={styles.flatList}
              renderItem={({item}) => {
                return <LastMinuteOptionTileCard key={item.id} item={item} />;
              }}
              horizontal
            />
          </ThemeProvider>
        </ScrollView>
      </ContentView>
    );
  },
);

export type ExclusiveOfferDetailScreenProps =
  RouteNavigatorScreenProps<AppRoute.EXCLUSIVE_OFFER_DETAIL> & {
    onOnlineOrderAfterSubmit?: () => void;
    onOnlineOrderCancel?: () => void;
    isLastCompetitionRemaining: boolean; // Bundy Power Plays
  };

const ExclusiveOfferDetailScreen = observer(
  ({route, navigation}: ExclusiveOfferDetailScreenProps) => {
    const rootStore = useStores();
    const {offersStore, userStore} = rootStore;
    const isLoggedIn = userStore.isLoggedIn();

    const [offerDetails, setOfferDetails] = useState<OfferDetailStore>();

    const offer = offersStore.data?.find(
      ({mflId}) => mflId === route.params.mflId,
    );

    useEffect(() => {
      if (offer) {
        setOfferDetails(new OfferDetailStore(offer, rootStore));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offer?.mflId]);

    // Handle deep linking "404". TODO: Better way? In "linking.ts"?
    useEffect(() => {
      if (!route.params?.mflId) {
        navigation.navigate(AppRoute.HOME);
      }
    }, [navigation, route.params]);

    useEffect(() => {
      if (!isLoggedIn) {
        navigation.navigate(AppRoute.HOME);
      }
    }, [isLoggedIn, navigation]);

    return (
      <ExclusiveOfferDetailScreenStandalone
        isLoading={offersStore.isLoading}
        offerDetails={offerDetails}
        navigation={navigation}
      />
    );
  },
);

function ItemSeparatorComponent() {
  return <View style={{width: 8, height: 8}} />;
}

export default ExclusiveOfferDetailScreen;

const styles = StyleSheet.create({
  textCard: {
    marginTop: 24,
  },
  termsAndConditions: {
    marginTop: 16,
    alignItems: 'center',
  },
  termsAndConditionsLink: {
    fontSize: 14,
    color: theme.colour.text.link.primary,
  },
  enterText: {
    fontSize: 16,
    color: 'white',
  },
  headline: {
    fontSize: 20,
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  flatList: {
    marginTop: 32,
    marginBottom: 4,
  },
  enterHereButton: {
    width: '80%',
  },
  copy: {
    fontSize: 16,
  },
  linkedProductsActivityIndicator: {
    flex: 1,
  },
});
